import React, { useEffect, useState } from "react";
import HRTitleHeader from "src/components/ui/HRTitleHeader";
import PayrollForm from "./components/PayrollForm";
import { useLocation, useSearchParams } from "react-router-dom";
import PayrollTable from "./components/PayrollTable";
import { GenericPagination } from "src/components/ui/pagination";
import { toast } from "react-toastify";
import { updateSearchParams } from "src/utils";
import MonthYearFilter from "src/components/ui/MonthYearFilter";
import Search from "src/components/input/Search";
import { Trash } from "lucide-react";
import { useDebounce } from "use-debounce";
import { PayrollFilters, usePayroll } from "./hooks/usePayroll";

const PayrollManagement: React.FC = () => {
  const location = useLocation();
  const employee = location.state?.employee;

  const [searchParams, setSearchParams] = useSearchParams();

  const employee_code = searchParams.get("employee_code") || "";
  const month = searchParams.get("month") || "";
  const year = searchParams.get("year") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  const [debouncedEmpCode] = useDebounce(employee_code, 500);

  const [failSafePageNumber, setFailSafePageNumber] = useState<number>(1);

  const filters: PayrollFilters = {
    page,
    month,
    year,
    employee_code: debouncedEmpCode,
  };

  const { data: payroll = [], isLoading } = usePayroll(filters);

  const [showForm, setShowForm] = useState(employee ? true : false);

  const handleClear = () => {
    setSearchParams();
  };

  useEffect(() => {
    if (!payroll) return;
    if (payroll?.length > 0) {
      setFailSafePageNumber(page);
    } else if (failSafePageNumber !== page) {
      toast.info("No more Payroll!");
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: failSafePageNumber.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payroll]);

  const handleNextPage = () => {
    setFailSafePageNumber(page);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (page === 1) {
      toast.info("This is the first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  return (
    <div className="space-y-6 pt-6">
      <div className="sticky top-0 backdrop-blur-lg">
        <HRTitleHeader
          title="Payroll Management"
          info="Process payroll, view payslips and tax reports"
        />

        <div className="flex justify-between items-center rounded-xl">
          <div className="flex gap-4 items-center w-[70%]">
            <MonthYearFilter
              month={month}
              year={year}
              onChange={({ month, year }) => {
                updateSearchParams(
                  "month",
                  month,
                  setSearchParams,
                  searchParams,
                );
                updateSearchParams("year", year, setSearchParams, searchParams);
              }}
            />
            <Search
              value={employee_code}
              placeholder="Search by Employee Code..."
              onChange={(value) =>
                updateSearchParams(
                  "employee_code",
                  value,
                  setSearchParams,
                  searchParams,
                )
              }
            />
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-2 h-10 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 transition"
            >
              <Trash />
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <PayrollForm employee={employee} onSuccess={() => setShowForm(false)} />
      )}

      <div>
        {isLoading && (
          <div className="text-center text-gray-500 mt-10 flex items-center justify-center gap-2 w-full">
            <h2>Loading Payrolls...</h2>
            <span className="loading loading-dots loading-md"></span>
          </div>
        )}
        {payroll?.length > 0 && (
          <div>
            <PayrollTable payroll={payroll} />
            
            <div className="flex-grow my-3">
              <GenericPagination
                currentPage={page}
                onNext={handleNextPage}
                onPrev={handlePrevPage}
                jumpPage={handleJumpToPage}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollManagement;
